import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { sets } from '../utils/api';

const ShareSetModal = ({ isOpen, onClose, setId }) => {
  useEscapeKey(onClose, isOpen);
  const [shareInfo, setShareInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && setId) {
      loadShareInfo();
    }
  }, [isOpen, setId]);

  const loadShareInfo = async () => {
    try {
      setLoading(true);
      const data = await sets.get(setId);
      setShareInfo(data.share_info);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateShare = async () => {
    try {
      setLoading(true);
      await sets.createShare(setId);
      await loadShareInfo(); // Reload share info after creating
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteShare = async () => {
    try {
      setLoading(true);
      await sets.deleteShare(setId);
      setShareInfo(null);
      setError(null);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    const shareUrl = `${window.location.origin}/shared/${shareInfo.share_id}`;
    navigator.clipboard.writeText(shareUrl);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-lg max-w-lg w-full p-6 shadow-xl"
        >
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-xl font-semibold">Share Set</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : error ? (
            <div className="text-red-600 mb-4">{error}</div>
          ) : shareInfo?.share_id ? (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg break-all">
                <p className="text-sm text-gray-500 mb-1">Share Link:</p>
                <p className="font-mono text-sm">
                  {`${window.location.origin}/shared/${shareInfo.share_id}`}
                </p>
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={handleCopyLink}
                  className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors"
                >
                  Copy Link
                </button>
                <button
                  onClick={handleDeleteShare}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors"
                >
                  Delete Share Link
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-gray-600">
                Create a public share link to allow anyone to view this set.
              </p>
              <button
                onClick={handleCreateShare}
                className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors"
              >
                Create Share Link
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ShareSetModal;
